import axios from "axios";
import axiosApiInstance from "../../helpers/interceptor";
import { use } from "echarts";

const baseUrl = import.meta.env.VITE_APP_BASE_URL || "";
let loginMethod = "auth";
let clientTokenMethod = "auth/loginAsSubClient";
let createTotpPath = "backOffice/totp/createTotp";
let validateTotpPath = "backOffice/totp/validateTotp";
let setTotpExcludedPath = "backOffice/totp/setExcluded";
let deleteTotpPath = (userId: string) => `backOffice/totp/deleteTotp/${userId}`;

export interface Token {
  token: string;
  loggedIn: boolean;
  errorMessage: string;
  redirectToTotpValidation?: boolean;
  redirectToTotpGeneration?: boolean;
}
export interface TokenRecovery {
  mensage: string;
  errorMessage: string;
}

const login = async (username: string, password: string): Promise<Token> => {
  try {
    let loginRequest = { username, password };
    let loginData = await axios.post<Token>(
      `${baseUrl}/${loginMethod}`,
      loginRequest
    );

    if (loginData.status === 200) {
      return {
        loggedIn: true,
        token: loginData.data.token,
        errorMessage: "",
        redirectToTotpValidation: loginData.data.redirectToTotpValidation,
        redirectToTotpGeneration: loginData.data.redirectToTotpGeneration,
      };
    } else {
      return {
        loggedIn: false,
        token: "",
        errorMessage: "Hubo un error conectandote",
      };
    }
  } catch (err: any) {
    console.error(err);
    return {
      loggedIn: false,
      token: "",
      errorMessage: "El usuario o la contraseña NO son correctas",
    };
  }
};

const recoverEmail = async (email: string): Promise<TokenRecovery> => {
  try {
    let loginRequest = { email };
    let loginData = await axios.post<TokenRecovery>(
      `${baseUrl}/users/recoverPassword`,
      loginRequest
    );

    if (loginData.status === 200) {
      return {
        mensage: "",
        errorMessage:
          "Se envió a tu correo electrónico un enlace para la recuperación de la contraseña.",
      };
    } else {
      return {
        mensage: "",
        errorMessage: "Hubo un error conectandote",
      };
    }
  } catch (err: any) {
    console.error(err);
    return {
      mensage: "",
      errorMessage: "El usuario NO existe",
    };
  }
};

const checkResetPasswordTokenAPI = async (
  token: string
): Promise<{ errorMessage: string; valid: boolean }> => {
  try {
    let req: CheckResetTokenRequest = { token };
    let checkResetPasswordToken = await axios.post<CheckResetTokenResponse>(
      `${baseUrl}/users/checkResetPasswordToken`,
      req
    );

    if (checkResetPasswordToken.data.correct) {
      return { errorMessage: "", valid: true };
    } else {
      return { errorMessage: "El token no es válido", valid: false };
    }
  } catch (err: any) {
    if (err.response === undefined)
      return { errorMessage: "Hubo un error inesperado", valid: false };
    if (err.response.status === 401) {
      return { errorMessage: "El token no es válido", valid: false };
    } else if (err.response.status === 403) {
      return { errorMessage: "El token no es válido", valid: false };
    } else if (err.response.status === 500) {
      return { errorMessage: "Hubo un error conectandote", valid: false };
    } else {
      return { errorMessage: "Hubo un error inesperado", valid: false };
    }
  }
};

const resetPasswordAPI = async (
  token: string,
  newPassword: string
): Promise<{ errorMessage: string; valid: boolean }> => {
  try {
    let req: ResetPasswordRequest = { token, newPassword };
    let resetPassword = await axios.post<boolean>(
      `${baseUrl}/users/resetPasswordWithToken`,
      req
    );

    if (resetPassword.data) {
      return { errorMessage: "", valid: true };
    } else {
      return { errorMessage: "Hubo un error conectandote", valid: false };
    }
  } catch (err: any) {
    if (err.response === undefined)
      return { errorMessage: "Hubo un error inesperado", valid: false };
    if (err.response.status === 401) {
      return { errorMessage: "El token no es válido", valid: false };
    } else if (err.response.status === 500) {
      return { errorMessage: "Hubo un error conectandote", valid: false };
    } else {
      return { errorMessage: "Hubo un error inesperado", valid: false };
    }
  }
};

export const getClientTokenAPI = async (clientId: string): Promise<Token> => {
  try {
    let loginData = await axiosApiInstance.get<Token>(
      `${baseUrl}/${clientTokenMethod}/${clientId}`
    );

    return { loggedIn: true, token: loginData.data.token, errorMessage: "" };
  } catch (err: any) {
    console.error(err);
    return {
      loggedIn: false,
      token: "",
      errorMessage: "El usuario o la contraseña NO son correctas",
    };
  }
};

/**
 * Crea un TOTP para el usuario con el token dado, el token debe ser un token de Generate TOTP
 * @param userId ID del usuario al que se le va a crear el TOTP
 * @returns
 */
export const createTotpAPI = async (
  token: string
): Promise<CreateTotpResponse | null> => {
  try {
    let createTotp = await axios.get<CreateTotpResponse>(
      `${baseUrl}/${createTotpPath}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return createTotp.data;
  } catch (err: any) {
    console.error(err);
    return null;
  }
};

/**
 * Verifica el TOTP del usuario con el token dado, el token debe ser un token de Verify TOTP
 * @param req Request con el codigo TOTP a validar
 * @param token Token del usuario que esta validando el TOTP
 * @returns
 */
export const verifyTotpAPI = async (
  req: ValidateTotpRequest,
  token: string
): Promise<Token> => {
  try {
    let validateTotp = await axios.post<Token>(
      `${baseUrl}/${validateTotpPath}`,
      req,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    return {
      loggedIn: true,
      token: validateTotp.data.token,
      errorMessage: "",
    };
  } catch (err: any) {
    console.error(err);
    return {
      loggedIn: false,
      token: "",
      errorMessage: "El código no es válido",
    };
  }
};

/**
 * Modifica la exclusión del TOTP para el usuario
 * @param req  Request con la exclusión y el ID del usuario
 * @returns  true si se modificó correctamente, false en caso contrario
 */
export const setExcludedAPI = async (
  req: SetExcludedRequest
): Promise<boolean> => {
  try {
    let setExcluded = await axiosApiInstance.post(
      `${setTotpExcludedPath}`,
      req
    );
    return setExcluded.status === 200;
  } catch (err: any) {
    console.error(err);
    return false;
  }
};

/**
 * Elimina el TOTP para el usuario con el ID dado
 * @param userId ID del usuario al que se le va a eliminar el TOTP
 * @returns true si se eliminó correctamente, false en caso contrario
 */
export const deleteTotpAPI = async (userId: string): Promise<boolean> => {
  try {
    let deleteTotp = await axiosApiInstance.delete<boolean>(
      `${deleteTotpPath(userId)}`
    );
    return deleteTotp.data;
  } catch (err: any) {
    console.error(err);
    return false;
  }
};

export interface CheckResetTokenRequest {
  token: string;
}

export interface CheckResetTokenResponse {
  correct: boolean;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

export interface CreateTotpResponse {
  uri: string;
  secret: string;
  validateToken: string;
}

export interface SetExcludedRequest {
  excluded: boolean;
  userId: string;
}

export interface ValidateTotpRequest {
  code: string;
}

export interface NewLogin {
  token: string;
}

export { login, recoverEmail, checkResetPasswordTokenAPI, resetPasswordAPI };
